import { useRef, useState, type KeyboardEvent } from "react";
import {
  formatCurrencyGbp,
  formatCurrencyGbpCompact,
} from "@shared-core/domain/formatters";
import type { SpendChart } from "@shared-core/domain/customerSpend";
import { cn } from "../../../app/components/primitives/utils";

// The month-by-month chart. Plain HTML bars rather than a charting library: the portal ships no chart
// dependency, and a customer-facing bundle should not gain one for a dozen bars. Every bar is a button, so a
// tap on a phone, a hover with a mouse and the arrow keys all reach the month's exact total. The group is a
// single tab stop (roving tabindex) so a keyboard user is not made to tab through a dozen bars to reach the
// breakdown below. The scale and the labels come from shared-core (`shapeChart`).

const CHART_HEIGHT_CLASS = "h-44";

export function MonthlyBarChart({
  chart,
  label,
}: {
  chart: SpendChart;
  /** The accessible name of the chart as a whole. */
  label: string;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const lastIndex = chart.bars.length - 1;
  const tabStop = selected ?? lastIndex;
  const shown = hovered ?? selected;
  const shownBar = shown === null ? null : (chart.bars[shown] ?? null);

  function move(to: number) {
    const clamped = Math.max(0, Math.min(lastIndex, to));
    setSelected(clamped);
    buttons.current[clamped]?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const keys: Record<string, number> = {
      ArrowRight: tabStop + 1,
      ArrowLeft: tabStop - 1,
      Home: 0,
      End: lastIndex,
    };
    const to = keys[event.key];
    if (to === undefined) return;
    event.preventDefault();
    move(to);
  }

  return (
    <div>
      <p aria-live="polite" className="mb-3 min-h-5 text-sm">
        {shownBar ? (
          <>
            <span className="font-medium">{shownBar.longLabel}</span>
            <span className="ml-2 tabular-nums">
              {formatCurrencyGbp(shownBar.total)}
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">
            Hover over or tap a month for its total
          </span>
        )}
      </p>

      <div className="flex gap-2">
        <div
          aria-hidden="true"
          className={cn(
            "relative w-10 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground",
            CHART_HEIGHT_CLASS,
          )}
        >
          {chart.ticks.map((tick) => (
            <span
              key={tick.fraction}
              className="absolute right-0 -translate-y-1/2"
              style={{ top: `${(1 - tick.fraction) * 100}%` }}
            >
              {formatCurrencyGbpCompact(tick.value)}
            </span>
          ))}
        </div>

        <div className="min-w-0 flex-1">
          <div className={cn("relative", CHART_HEIGHT_CLASS)}>
            {chart.ticks.map((tick) => (
              <div
                key={tick.fraction}
                aria-hidden="true"
                className="absolute inset-x-0 border-t border-dashed border-border"
                style={{ top: `${(1 - tick.fraction) * 100}%` }}
              />
            ))}
            <div
              role="group"
              aria-label={label}
              onKeyDown={onKeyDown}
              onPointerLeave={() => setHovered(null)}
              className="absolute inset-0 flex items-stretch gap-[3px] sm:gap-1.5"
            >
              {chart.bars.map((bar, index) => (
                <button
                  key={bar.month}
                  ref={(el) => {
                    buttons.current[index] = el;
                  }}
                  type="button"
                  tabIndex={index === tabStop ? 0 : -1}
                  aria-label={`${bar.longLabel}: ${formatCurrencyGbp(bar.total)}`}
                  aria-pressed={selected === index}
                  onClick={() => setSelected(index)}
                  onPointerEnter={() => setHovered(index)}
                  onFocus={() => {
                    setHovered(null);
                    setSelected(index);
                  }}
                  className="flex min-w-0 flex-1 items-end rounded-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <span
                    className={cn(
                      "block w-full rounded-t-sm transition-colors",
                      shown === index || shown === null
                        ? "bg-primary"
                        : "bg-primary/45",
                    )}
                    style={{
                      height: `${Math.max(bar.height * 100, bar.total > 0 ? 1.5 : 0)}%`,
                    }}
                  />
                </button>
              ))}
            </div>
          </div>

          <div aria-hidden="true" className="mt-1.5 flex gap-[3px] sm:gap-1.5">
            {chart.bars.map((bar) => (
              <div
                key={bar.month}
                className="min-w-0 flex-1 text-center text-[10px] leading-tight text-muted-foreground"
              >
                <span className="sm:hidden">{bar.shortLabel.slice(0, 1)}</span>
                <span className="hidden sm:inline">{bar.shortLabel}</span>
                <span className="block h-3 whitespace-nowrap text-left font-medium">
                  {bar.yearLabel}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
